// @flow

import config from "@config";
import actionCreators from "@modules/reporting/actionCreator";
import { ReportModalStepTypes } from "@modules/reporting/constants/reports";
import type {
  AddEditModalConfig,
  AddEditReportModalType,
  DefaultReport,
  MySavedReport,
  SaveReportModalType,
} from "@modules/reporting/reducer/modals/types";
import { getAddEditReportModal, getSaveReportModal } from "@modules/reporting/selectors/modals";
import * as selectors from "@modules/reporting/selectors/reports/absence-details";
import type { State } from "@store/reducers";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import AddEditReportModal from "../add-edit-report-modal";
import SaveReportModal from "../save-report-modal";
import StepFiltersView from "./step-filters-view";

type OwnProps = $Exact<{
  clientId: number,
  columns: string[],
  returnReportToSave?: (data: MySavedReport) => void,
  getReport: (data: MySavedReport) => () => void,
  onClose: Function,
}>;

type PropsFromState = $Exact<{
  schema: Object,
  addEditModal: AddEditReportModalType,
  saveReportModal: SaveReportModalType,
}>;

type PropsFromDispatch = $Exact<{
  getAbsenceRecordSearchFiltersSchema: typeof actionCreators.GetAbsenceRecordSearchFiltersSchema,
  createAbsenceSavedReport: typeof actionCreators.CreateAbsenceSavedReport,
  updateSavedAbsenceDetailsReport: typeof actionCreators.UpdateSavedAbsenceDetailsReport,
  setReportToSave: typeof actionCreators.SetAbsenceDetailsReportToSave,
  toggleAddEditReportModal: typeof actionCreators.ToggleAddEditReportModal,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

export function AbsenceDetailsAddEditReportModal(props: Props) {
  useEffect(() => {
    props.getAbsenceRecordSearchFiltersSchema({ clientId: props.clientId });
  }, []);

  function onUpdateAbsenceSavedReport(data: MySavedReport) {
    if (props.onClose) {
      props.onClose();
    }

    if (props.returnReportToSave) {
      props.returnReportToSave(data);
      props.toggleAddEditReportModal({ visible: false });
      return;
    }

    props.updateSavedAbsenceDetailsReport({
      clientId: props.clientId,
      reportId: data.id,
      data: data,
    });
    props.toggleAddEditReportModal({ visible: false });
  }

  function onCreateAbsenceSavedReport(data: DefaultReport) {
    props.createAbsenceSavedReport({
      clientId: props.clientId,
      ...data,
    });
  }

  function onGetReport(data: MySavedReport) {
    if (!props.getReport) {
      return;
    }

    props.getReport(getTransformedData(data))();
    props.setReportToSave({
      ...data,
      isNew: true,
    });
    props.toggleAddEditReportModal({ visible: false });
  }

  function getTransformedData(data: DefaultReport | MySavedReport) {
    return {
      ...data,
      filters: {
        ...data.filters,
        absencesGreaterThanDays:
          data.filters.absencesGreaterThanDays === 0 ? null : data.filters.absencesGreaterThanDays,
      },

      columns: props.columns,
    };
  }

  function getFiltersView(data: Object) {
    return <StepFiltersView data={data} />;
  }

  function uiSchema() {
    const editReport = !!props.addEditModal.data;
    return {
      "ui:order": editReport
        ? [
            // Period:
            "dateRange",
            "fromDate",
            "toDate",
            // Status
            "absenceStatus",
            "absenceType",
            "absencesFilter",
            // Length
            "absenceLength",
            "absencesGreaterThanDays",
            "absencesGreaterThanDayType",
            // Reason
            "absenceReasons",
            "showConsentWithheldOnly",
            "showWorkRelatedOnly",
            // Employees
            "includeLeavers",
            "includeEmployeesWithDisability",
            "gender",
            "staffGroups",
            "ageGroups",
            "*",
          ]
        : [
            "dateRange",
            "absenceLength",
            "fromDate",
            "includeLeavers",
            "toDate",
            "includeEmployeesWithDisability",
            "absenceStatus",
            "gender",
            "absenceType",
            "showConsentWithheldOnly",
            "absencesFilter",
            "absenceReasons",
            "showWorkRelatedOnly",
            "staffGroups",
            "absencesGreaterThanDays",
            "ageGroups",
            "absencesGreaterThanDayType",
            "employeesWithDisabilities",
            "*",
          ],
      // Period
      dateRange: {
        "ui:header": editReport && props.t("PERIOD"),
        "ui:options": {
          tooltip: {
            description: props.t("DATE_RANGE_FILTER_TOOLTIP"),
            id: "DATE_RANGE",
          },
        },
      },
      fromDate: {
        "ui:title": props.t("FROM_DATE"),
        "ui:widget": "date-picker",
        "ui:options": {
          tooltip: {
            title: props.t("FROM_DATE"),
            description: props.t("FROM_DATE_TOOLTIP"),
            id: "FROM_DATE",
            size: "medium",
          },
          dateFormat: config.dateFormatBE,
        },
      },
      toDate: {
        "ui:title": props.t("TO_DATE"),
        "ui:widget": "date-picker",
        "ui:options": {
          tooltip: {
            title: props.t("TO_DATE"),
            description: props.t("TO_DATE_TOOLTIP"),
            id: "TO_DATE",
            size: "medium",
          },
          dateFormat: config.dateFormatBE,
        },
      },
      // Status
      absenceStatus: {
        "ui:header": editReport && props.t("STATUS"),
        "ui:title": props.t("ABSENCE_STATUS"),
        "ui:options": {
          tooltip: {
            description: props.t("ABSENCE_STATUS_FILTER_TOOLTIP"),
            id: "ABSENCE_STATUS",
            size: "medium",
          },
        },
      },
      showConsentWithheldOnly: {
        "ui:widget": "select",
        "ui:title": props.t("CONSENT_WITHHELD_ONLY"),
      },
      absenceType: {
        "ui:options": {
          tooltip: {
            title: props.t("ABSENCE_TYPE"),
            description: props.t("ABSENCE_TYPE_FILTER_TOOLTIP"),
            id: "absence-type",
            size: "medium",
          },
        },
      },
      absencesFilter: {
        "ui:widget": "select",
        "ui:options": {
          tooltip: {
            title: props.t("ABSENCES_FILTER"),
            description: props.t("ABSENCES_FILTER_TOOLTIP"),
            id: "absences-filter",
            size: "medium",
          },
          bottomOffsetNone: true,
        },
      },
      // Length
      absenceLength: {
        "ui:header": editReport && props.t("LENGTH"),
        "ui:title": props.t("ABSENCE_LENGTH"),
        "ui:options": {
          tooltip: {
            description: props.t("ABSENCE_LENGTH_TOOLTIP"),
            id: "ABSENCE_LENGTH",
            size: "medium",
          },
        },
      },
      absencesGreaterThanDays: {
        "ui:title": props.t("ABSENCES_GREATER_THAN_DAYS"),
        "ui:options": {
          tooltip: {
            description: props.t("ABSENCES_GREATER_THAN_DAYS_TOOLTIP"),
            id: "ABSENCES_GREATER_THAN_DAYS",
            size: "medium",
          },
        },
        "ui:widget": "number-input",
      },
      absencesGreaterThanDayType: {
        "ui:title": props.t("ABSENCES_GREATER_THAN_DAY_TYPE"),
        "ui:options": {
          tooltip: {
            description: props.t("ABSENCES_GREATER_THAN_DAY_TYPE_TOOLTIP"),
            id: "ABSENCES_GREATER_THAN_DAY_TYPE",
            size: "medium",
          },
        },
      },
      // Reason
      absenceReasons: {
        "ui:header": editReport && props.t("REASON"),
        "ui:field": "multi-select",
        "ui:options": {
          tooltip: {
            title: props.t("ABSENCE_REASONS"),
            description: props.t("ABSENCE_REASONS_FILTER_TOOLTIP"),
            id: "ABSENCE_REASONS",
            size: "medium",
          },
        },
        items: {
          id: {
            "ui:options": {
              hidelabel: true,
            },
          },
        },
      },
      showWorkRelatedOnly: {
        "ui:title": props.t("REPORT_FILTER_WORK_RELATED_ONLY"),
        "ui:options": {
          tooltip: {
            description: props.t("SHOW_WORK_RELATED_ONLY_TOOLTIP"),
            id: "SHOW_WORK_RELATED_ONLY",
            size: "medium",
          },
        },
        "ui:widget": "select",
      },

      // Employees
      includeLeavers: {
        "ui:header": editReport && props.t("EMPLOYEES"),
        "ui:title": props.t("INCLUDE_LEAVERS"),
        "ui:options": {
          tooltip: {
            description: props.t("INCLUDE_LEAVERS_TOOLTIP"),
            id: "INCLUDE_LEAVERS",
            size: "medium",
          },
        },
        "ui:widget": "select",
      },
      includeEmployeesWithDisability: {
        "ui:title": props.t("INCLUDE_EMPLOYEES_WITH_DISABILITY"),
        "ui:options": {
          tooltip: {
            description: props.t("INCLUDE_EMPLOYEES_WITH_DISABILITY_TOOLTIP"),
            id: "INCLUDE_EMPLOYEES_WITH_DISABILITY",
            size: "medium",
          },
        },
        "ui:widget": "select",
      },
      gender: {
        "ui:title": props.t("GENDER"),
        "ui:widget": "select",
        "ui:options": {
          tooltip: {
            description: props.t("GENDER_TOOLTIP"),
            id: "GENDER",
            size: "medium",
          },
        },
      },
      staffGroups: {
        "ui:field": "multi-select",
        "ui:title": props.t("STAFF_GROUPS"),
        "ui:options": {
          tooltip: {
            description: props.t("STAFF_GROUPS_FILTER_TOOLTIP"),
            id: "STAFF_GROUPS",
            size: "medium",
          },
        },
        items: {
          id: {
            "ui:options": {
              hidelabel: true,
            },
          },
        },
      },
      ageGroups: {
        "ui:field": "multi-select",
        "ui:options": {
          tooltip: {
            title: props.t("AGE_GROUPS"),
            description: props.t("AGE_GROUPS_TOOLTIP"),
            id: "AGE_GROUPS",
            size: "medium",
          },
        },
        items: {
          "ui:options": {
            hidelabel: true,
          },
        },
      },
    };
  }

  const modalConfig: AddEditModalConfig = [
    {
      type: ReportModalStepTypes.FILTERS,
      title: {
        new: props.t("SET_FILTERS"),
        edit: props.t("FILTERS"),
      },
      schema: props.schema,
      uiSchema: uiSchema(),
      getView: getFiltersView,
      hideNextButton: true,
    },
  ];

  return (
    <React.Fragment>
      {props.addEditModal.visible && (
        <AddEditReportModal
          getReport={onGetReport}
          initialStep={props.addEditModal.initialStep}
          updateReport={onUpdateAbsenceSavedReport}
          clientId={props.clientId}
          modalConfig={modalConfig}
        />
      )}
      {props.saveReportModal.visible && (
        <SaveReportModal
          createReport={onCreateAbsenceSavedReport}
          updateReport={onUpdateAbsenceSavedReport}
        />
      )}
    </React.Fragment>
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  schema: selectors.absenceRecordSearchFiltersSchema(state),
  addEditModal: getAddEditReportModal(state),
  saveReportModal: getSaveReportModal(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getAbsenceRecordSearchFiltersSchema: actionCreators.GetAbsenceRecordSearchFiltersSchema,
  createAbsenceSavedReport: actionCreators.CreateAbsenceSavedReport,
  updateSavedAbsenceDetailsReport: actionCreators.UpdateSavedAbsenceDetailsReport,
  setReportToSave: actionCreators.SetAbsenceDetailsReportToSave,
  toggleAddEditReportModal: actionCreators.ToggleAddEditReportModal,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  localize(),
)(AbsenceDetailsAddEditReportModal);
